import { useState, useRef } from 'react'
import {
  Form as SemanticForm,
  Dropdown,
  StrictFormProps,
  StrictFormGroupProps,
  StrictCheckboxProps,
} from 'semantic-ui-react'
import styled, { css } from 'styled-components'
import { withTranslation } from 'react-i18next'

import { useBreakpoints, useTheme } from '@/hooks'
import Icon from '../Icon'
import TextArea from '../TextArea'
import DatePicker from '../DatePicker'
import Select, { type SelectProps } from '../Select'
import MobileDropdown, { type MobileDropdownProps } from '../MobileDropdown'

interface CheckboxProps extends StrictCheckboxProps {
  action?: boolean
  actionColor?: string
  actionColorContrast?: string
  checkedColor?: string
  uncheckedColor?: string
  agreementText?: string
  clearable?: boolean
  sensitive?: boolean
  onSensitiveToggle?: (e: React.MouseEvent, visible: boolean) => void
}

const StyledForm = styled(SemanticForm).attrs({
  noValidate: true,
})`
  ${({ theme }) => css`
    &&& {
      .field {
        width: 100%;

        .error {
          margin: 0;
        }
      }

      input:disabled,
      textarea:disabled,
      .ui.disabled.dropdown {
        background: ${theme.colors.backgroundColor};
      }

      .disabled,
      input:disabled,
      textarea:disabled {
        opacity: 1;
      }

      .disabled.checkbox {
        opacity: .7;
      }

      .input,
      .dropdown:not(.multiple) {
        min-height: 35px;
        max-height: 35px;
      }

      input,
      textarea,
      .ui.dropdown.dropdown {
        border: 1px solid #bdcbd0 !important;
        box-shadow: 0px 2px 12px -5px rgb(0 46 62 / 5%);
        min-width: 0;
      }

      input,
      textarea {
        font-family: Roboto;
        color: ${theme.colors.abyss} !important;
        font-weight: 300;

        ::placeholder {
          font-size: 14px;
          color: ${theme.colors.textGrey} !important;
        }
      }
      
      /* Chrome, Safari, Edge, Opera */
      input::-webkit-outer-spin-button,
      input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      /* Firefox */
      input[type=number] {
        -moz-appearance: textfield;
      }

      textarea {
        line-height: 23px;
      }

      .ui.dropdown > .dropdown.icon:not(.clear):before {
        /* icon-angle-down */
        content: '\\e01a';
        color: #002e3e;
        font-size: 20px;
      }

      .dropdown:not(.multiple) {
        display: flex;
        align-items: center;

        .divider:not(.default) {
          height: 35px;
          display: flex;
          align-items: center;

          div {
            text-overflow: ellipsis;
            overflow: hidden;
            padding: 4px 0;
          }
        }
      }

      .dropdown {
        height: auto;
        line-height: 14px;

        > .ui.label {
          white-space: normal !important;
        }

        .visible.menu {
          border: 1px solid #bdcbd0 !important;
          border-top: 0 !important;
          box-shadow: none !important;
        }

        .text {
          font-weight: 300;

          &:not().default {
            color: ${theme.colors.abyss};
          }
        }

        .item {
          background: #fff !important;

          &, span {
            color: ${theme.colors.abyss} !important;
          }

          span {
            background: none !important;
            color: ${theme.colors.abyss} !important;
          }

          :hover {
            background: ${theme.colors.lightGrey} !important;
          }
        }

        .active.item .text {
          font-weight: 400;
        }

        .image {
          width: 25px !important;
          height: 25px !important;
          object-fit: cover;
          vertical-align: middle;
          max-height: 100%;
        }

        &.icon {
          padding: 0.65em 0.3em !important;

          &.clear:before {
            /* map to icon-close-x */
            content: '\\6e' !important;
          }
        }

        &.icon {
          top: 0.7em !important;
        }

        & .addition .icon {
          top: 0.2em !important;
        }

        &.loading .icon:not(.delete) {
          top: 0.4em !important;
        }

        & .delete.icon {
          position: relative;
        }
      }

      .error {
        input[type='text'],
        .fr-box,
        .ui.dropdown,
        .ui.dropdown:hover {
          background: none !important;
          border: 1px solid #de5447 !important;
          color: initial !important;
        }

        .ui.dropdown,
        .ui.dropdown:hover {
          input[type='text'] {
            background: none !important;
            border: none !important;
            color: initial !important;
          }
        }

        .dropdown > .text {
          color: ${theme.colors.textGrey} !important;
          background none !important;
          font-weight: 300 !important;
        }

        .visible.menu {
          border-color: #de5447 !important;
        }

        word-break: keep-all;
      }

      &.loading.form:before {
        z-index: 10000;
      }

      &.loading.form:after {
        z-index: 10001;
      }

      ${({ theme }) => theme.mediaQueries.mobile} {
        width: 100%;
      }
    }
  `}
`

const Form = (props: StrictFormProps) => <StyledForm {...props} />

Form.Field = SemanticForm.Field

Form.Group = ({
  fluid,
  ...props
}: StrictFormGroupProps & { fluid?: boolean }) => (
  <SemanticForm.Group {...props} />
)

Form.Dropdown = withTranslation()(
  ({ t, i18nOptions, defaultNS, reportNS, tReady, ...props }) => (
    <SemanticForm.Dropdown
      scrolling={false}
      noResultsMessage={
        props.loading ? `${t('Loading')}...` : t('No results found')
      }
      {...props}
    />
  ),
)

// @ts-ignore
Form.Dropdown.Menu = Dropdown.Menu
// @ts-ignore
Form.Dropdown.Item = Dropdown.Item

// @ts-ignore
StyledForm.Select = function SelectWithMobileSupport(
  props: MobileDropdownProps | SelectProps,
) {
  const { isMobile } = useBreakpoints()
  if (isMobile) {
    return <MobileDropdown {...(props as MobileDropdownProps)} />
  }
  return <Select {...(props as SelectProps)} />
}

// @ts-ignore
StyledForm.Input = withTranslation()(
  ({
    sensitive,
    value,
    icon,
    disabled,
    t,
    i18nOptions,
    defaultNS,
    reportNS,
    tReady,
    autoComplete,
    type,
    clearable,
    onSensitiveToggle,
    ...rest
  }) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [visible, setVisible] = useState(false)

    const InputIcon = icon ? (
      <Icon
        size="big"
        name={icon?.name}
        style={{
          position: 'absolute',
          right: '8px',
          top: '8px',
        }}
      />
    ) : null

    const Input = (
      <SemanticForm.Input
        type={type}
        {...rest}
        value={sensitive && !visible ? '**********' : value}
        disabled={(sensitive && !visible) || disabled}
        autoComplete={autoComplete || 'off'}>
        <input
          ref={inputRef}
          data-lpignore="true"
          style={{
            height: '35px',
            background: !disabled ? '#fff' : undefined,
          }}
        />
        {InputIcon}
      </SemanticForm.Input>
    )

    if (sensitive) {
      return (
        <div title={t('Toggle content')} style={{ position: 'relative' }}>
          {Input}
          <Icon
            button
            size="big"
            name={visible ? 'icon-sensitive-off' : 'icon-sensitive-on'}
            onClick={e => {
              onSensitiveToggle?.(e, !visible)
              setVisible(!visible)

              if (!visible) {
                setTimeout(() => {
                  inputRef.current?.focus?.()
                }, 0)
              }
            }}
            style={{
              position: 'absolute',
              right: '8px',
              top: '8px',
            }}
          />
        </div>
      )
    }

    return Input
  },
)

Form.TextArea = TextArea

// @ts-ignore
StyledForm.DatePicker = DatePicker

const CheckboxWrapper = styled(({ sensitive, radio, ...rest }) => (
  <div {...rest} />
))<{ sensitive?: boolean; radio?: boolean }>`
  ${({ sensitive, radio }) => css`
    display: flex;
    align-items: center;
    ${radio &&
    css`
      label::before {
        background: #fff !important;
      }
      label::after {
        background: ${props => props.theme.primaryColor} !important;
      }
    `}

    ${sensitive &&
    css`
      label::before {
        opacity: 0;
      }

      label::after {
        content: '*' !important;
        opacity: 1 !important;
        top: 2px !important;
      }
    `}
  `}
`

StyledForm.Checkbox = styled(
  ({
    action,
    clearable,
    agreementText,
    sensitive,
    disabled,
    onSensitiveToggle,
    radio,
    ...props
  }: CheckboxProps) => {
    const [visible, setVisible] = useState(false)
    const theme = useTheme()
    return (
      <CheckboxWrapper
        radio={radio}
        sensitive={sensitive && !visible}
        onClick={(e: React.MouseEvent) => {
          onSensitiveToggle?.(e, true)
          setVisible(true)
        }}>
        <SemanticForm.Checkbox
          theme={theme}
          radio={radio}
          disabled={disabled || (sensitive && !visible)}
          {...props}
        />
      </CheckboxWrapper>
    )
  },
)<CheckboxProps>`
  ${props =>
    `
    display:flex;
    align-items:center;
    input:checked ~ .box:before,
    .ui.checkbox input:checked ~ label:before {
      border-color: ${props.actionColor || props.theme.primaryColor};
      background: ${props.theme.primaryColor};
    }
  
    input:checked ~ .box:after,
    .ui.checkbox input:checked ~ label:after {
      opacity: 1;
      color: ${props.actionColorContrast || props.theme.secondaryFontColor};
    }
`};

  ${props =>
    props.toggle &&
    `
    .ui.toggle.checkbox input:focus:checked ~ .box:before,
    .ui.toggle.checkbox input:focus:checked ~ label:before {
      background-color: ${
        props.checkedColor || props.theme.primaryColor
      } !important;
    }

    .ui.toggle.checkbox input:checked ~ .box:before,
    .ui.toggle.checkbox input:checked ~ label:before {
      background-color: ${
        props.checkedColor || props.theme.primaryColor
      } !important;
    }

    .ui.toggle.checkbox .box:hover::before,
    .ui.toggle.checkbox label:hover::before {
      background-color: ${props.uncheckedColor} !important;
      filter: brightness(80%);
    }

    .ui.toggle.checkbox .box:before,
    .ui.toggle.checkbox label:before {
      background-color: ${props.uncheckedColor} !important;
    }
  `};
`

export default StyledForm
